import { useCallback, useEffect, useState } from "react";
import { customGoal, goalWithValue, stage } from "@flights/et-universal";
import { useSelector } from "react-redux";
import {
  getSearchFetchStatus,
  getFlexibleDateMinPriceFetchStatus,
  getSearchResultsFlightDeals
} from "store/searchResults/selectors";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import useRouteName from "hooks/useRouteName";

export default function useTrackFlightsFlexiBannerSourceChange() {
  const sessionKey = "flexiClickedPriceValue";
  const expTagName = "flights_web_flexi_banner_source_change_ml";
  const routeName = useRouteName();
  const [isTracked, setIsTracked] = useState(false);
  const [canTrack, setCanTrack] = useState(false);
  const searchResultsFetchStatus = useSelector(getSearchFetchStatus);
  const flexibleDateMinPriceFetchStatus = useSelector(getFlexibleDateMinPriceFetchStatus);
  const searchResultsFlightDeals = useSelector(getSearchResultsFlightDeals);

  const setClickedPrice = useCallback((price: string) => {
    flightsSessionStore.set(sessionKey, price, { ttl: "10m" });
  }, []);

  const getClickedPrice = useCallback(() => {
    return Number(flightsSessionStore.get(sessionKey) || 0);
  }, []);

  const removeClickedPrice = useCallback(() => {
    flightsSessionStore.unset(sessionKey);
  }, []);

  const trackStages = useCallback(() => {
    if (isTracked || !canTrack) {
      return;
    }

    stage(expTagName, 2);

    const clickedPrice = getClickedPrice();

    const bestOffer = searchResultsFlightDeals?.find(({ key }) => key === "BEST");
    const bestPricePerAdult = bestOffer?.travellerPrices?.find(({ travellerType }) => travellerType === "ADULT")
      ?.travellerPriceBreakdown.totalRounded;

    const cheapestOffer = searchResultsFlightDeals?.find(({ key }) => key === "CHEAPEST");
    const cheapestPricePerAdult = cheapestOffer?.travellerPrices?.find(({ travellerType }) => travellerType === "ADULT")
      ?.travellerPriceBreakdown.totalRounded;

    if (!clickedPrice) {
      return;
    }

    if (searchResultsFetchStatus === "success") {
      customGoal(expTagName, 5);
    }

    if (bestPricePerAdult || cheapestPricePerAdult) {
      const bestPriceNum = bestPricePerAdult ? Number(`${bestPricePerAdult.units}.${bestPricePerAdult.nanos}`) : 0;
      const cheapestPriceNum = cheapestPricePerAdult
        ? Number(`${cheapestPricePerAdult.units}.${cheapestPricePerAdult.nanos}`)
        : 0;

      if (bestPriceNum) {
        if (bestPriceNum > clickedPrice) {
          customGoal(expTagName, 1);
          goalWithValue(`${expTagName}_best_above_clicked`, 1);
        }
        if (bestPriceNum < clickedPrice) {
          customGoal(expTagName, 2);
          goalWithValue(`${expTagName}_best_below_clicked`, 1);
        }
      }

      if (cheapestPriceNum) {
        if (cheapestPriceNum > clickedPrice) {
          customGoal(expTagName, 3);
          goalWithValue(`${expTagName}_cheapest_above_clicked`, 1);
        }
        if (cheapestPriceNum < clickedPrice) {
          customGoal(expTagName, 4);
          goalWithValue(`${expTagName}_cheapest_below_clicked`, 1);
        }
      }

      removeClickedPrice();
      setIsTracked(true);
    }
  }, [isTracked, canTrack, searchResultsFlightDeals, getClickedPrice, removeClickedPrice, searchResultsFetchStatus]);

  useEffect(() => {
    if (routeName.includes("searchresults") && flexibleDateMinPriceFetchStatus === "success") {
      setCanTrack(true);
    }
  }, [flexibleDateMinPriceFetchStatus, routeName, setCanTrack]);

  return {
    setClickedPrice,
    trackStages
  };
}
